import { DatasetMultipleValuesSet } from '@/shared/data/model/DatasetMultipleValuesSet';
import { DatasetValueSet } from '@/shared/data/model/DatasetValueSet';
import _ from 'lodash';
import Vue, { getCurrentInstance } from 'vue';
import Vuex, { Store } from 'vuex';
import Dataset from '../../../data/model/Dataset';
import DatasetValue from '../../../data/model/DatasetValue';
import Datasets from '../../../data/model/Datasets';
import Proxies from '../../../data/model/Proxies';
import { EventBus } from '../../connector/EventBus';
import AppConstants from '../AppConstants';
import Page2 from '../templating/Page2';
import Actions from './actions';
import Mutations from './mutations';
import State from './state';

Vue.use(Vuex);

export const makeStore = async (page: Page2) => {
  const store = new Vuex.Store({
    state: {
      datasets: {},
      forced: {},
      defaults: {},
      proxies: {},
      resizeUpdateSwitch: false,
    },
    getters: {
      /**
       * Getter : lire une valeur d'un dataset
       */
      value:
        (state: State) =>
        (name: string, key: string): DatasetValue | undefined => {
          return _.get(state, `datasets.${name}.${key}`);
        },
      all:
        (state: State) =>
        (name: string): Dataset => {
          return state.datasets[name] || {};
        },
      indexed:
        (state: State) =>
        (name: string, keysPrefix: string): Dataset[] => {
          const dataset = state.datasets[name];
          const ret: Dataset[] = [];
          if (dataset) {
            const rx = new RegExp(`^${keysPrefix}(\\d+)_(\\w+)$`);
            // Récupération de toutes les clées qui match le préfixe + tri
            const matches = Object.keys(dataset)
              .sort()
              .map((key) => key.match(rx))
              .filter((match) => match !== null);
            let previousIndex = '-1';
            matches.forEach((match) => {
              if (!match) return;
              const index = match[1];
              let e: Dataset = {};
              if (previousIndex === index) e = ret[ret.length - 1];
              else ret.push(e);
              e[match[2]] = dataset[match[0]];
              previousIndex = index;
            });
          }
          return ret;
        },
      forced:
        (state: State) =>
        (name: string, key: string): DatasetValue | null => {
          return _.get(state, `forced.${name}.${key}`, null);
        },
      default:
        (state: State) =>
        (name: string, key: string): DatasetValue | null => {
          return _.get(state, `defaults.${name}.${key}`, null);
        },
      template:
        page.templateGetter ||
        (() => {
          return {};
        }),
      online: (state: State): boolean => {
        return Boolean(state.datasets.$app?.online) === true;
      },
      rootFontSize: (state): number => {
        // eslint-disable-next-line no-unused-expressions
        state.resizeUpdateSwitch;
        return Math.round(0.008 * Math.max(window.innerHeight, window.innerWidth));
      },
    },
    mutations: {
      /**
       * Mutation : écrire une valeur dans un dataset
       */
      [Mutations.DATASET_SET](state: State, datasetValue: DatasetValueSet) {
        if (!datasetValue) return;
        if (state.datasets[datasetValue.name] === undefined) {
          Vue.set(state.datasets, datasetValue.name, {});
        }
        Vue.set(state.datasets[datasetValue.name], datasetValue.key, datasetValue.value);
        EventBus.$emit(AppConstants.BACKDOOR_EVENT_NAME);
      },
      /**
       * Mutation : renseigner les valeurs forcées
       */
      [Mutations.FORCED_VALUES_SET](state: State, forcedValues: Datasets) {
        if (forcedValues) state.forced = forcedValues;
      },
      /**
       * Mutation : renseigner les valeurs par défaut
       */
      [Mutations.DEFAULT_VALUES_SET](state: State, defaultValues: Datasets) {
        if (defaultValues) state.defaults = defaultValues;
      },
      /**
       * Mutation : renseigner les valeurs proxy
       */
      [Mutations.PROXIED_VALUES_SET](state: State, proxies: Proxies) {
        state.proxies = proxies;
      },
      /**
       * Mutation : basculer le switch de mise à jour
       */
      [Mutations.UPDATE_SWITCH_CHANGE](state: State) {
        state.resizeUpdateSwitch = !state.resizeUpdateSwitch;
      },
    },
    actions: {
      [Actions.DATASET_SET]({ state, dispatch, getters, commit }, setValue: DatasetValueSet) {
        // Si la valeur est forcée : terminé
        if (getters.forced(setValue.name, setValue.key) !== null) return;

        // Si la valeur n'existe pas, ou si l'overwrite est autorisé
        if (getters.value(setValue.name, setValue.key) === undefined || !setValue.doNotOverwrite) {
          let valueToSet = setValue.value;
          if (valueToSet === 'b:true') valueToSet = true;
          if (valueToSet === 'b:fasle') valueToSet = false;

          // Si le dataset n'existe pas encore, il est créé
          if (getters.all(setValue.name) === undefined) Vue.set(state.datasets, setValue.name, {});

          // Mise à jour des proxies
          // Recherche des valeurs ayant pour cible proxy cette valeur
          Object.keys(state.proxies).forEach((path) => {
            if (state.proxies[path] === `${setValue.name}.${setValue.key}`) {
              // si cible = cette valeur, alors set
              const [sourceName, sourceKey] = path.split('.');
              if (sourceName && sourceKey) {
                dispatch(Actions.DATASET_SET, {
                  name: sourceName,
                  key: sourceKey,
                  value: setValue.value,
                });
              }
            }
          });

          commit(Mutations.DATASET_SET, {
            name: setValue.name,
            key: setValue.key,
            value: valueToSet,
          });
        }
      },
      [Actions.DATASET_SET_MULTIPLE]({ dispatch }, setValues: DatasetMultipleValuesSet) {
        if (setValues.name && setValues.values) {
          Object.keys(setValues.values).forEach((key) => {
            const value = setValues.values[key];
            if (value !== undefined) {
              dispatch(Actions.DATASET_SET, {
                name: setValues.name,
                key,
                value,
                doNotOverwrite: setValues.doNotOverwrite,
              } as DatasetValueSet);
            }
          });
        }
      },
      [Actions.FORCED_VALUES_SET]({ commit, dispatch }, forcedValues: Datasets) {
        // Clear de la config actuelle pour permettre les SET
        commit(Mutations.FORCED_VALUES_SET, {});

        if (forcedValues) {
          // Set des valeurs
          Object.keys(forcedValues).forEach((datasetName) => {
            const dataset = { ...forcedValues[datasetName] };
            if (dataset) {
              dispatch(Actions.DATASET_SET_MULTIPLE, {
                name: datasetName,
                values: dataset,
              });
            }
          });
          // Sauvegarde des nouvelles valeurs forcées
          commit(Mutations.FORCED_VALUES_SET, forcedValues);
        }
      },
      [Actions.DEFAULT_VALUES_SET]({ commit, dispatch }, defaultValues: Datasets) {
        // Sauvegarde de la configuration
        if (defaultValues) {
          // Set des valeurs
          Object.keys(defaultValues).forEach((datasetName) => {
            const dataset = { ...defaultValues[datasetName] };
            if (datasetName) {
              dispatch(Actions.DATASET_SET_MULTIPLE, {
                name: datasetName,
                values: dataset,
                // doNotOverwrite: true,
              });
            }
          });
          // Sauvegarde des nouvelles valeurs forcées
          commit(Mutations.DEFAULT_VALUES_SET, defaultValues);
        }
      },
      [Actions.PROXIED_VALUES_SET]({ commit }, proxies: Proxies) {
        commit(Mutations.PROXIED_VALUES_SET, { ...proxies });
      },
    },
    modules: {},
  });

  // Injection des datas >==!==<
  // Valeurs forcées
  if (page.force) store.dispatch(Actions.FORCED_VALUES_SET, page.force);

  // Proxies
  if (page.proxies) store.dispatch(Actions.PROXIED_VALUES_SET, page.proxies);

  // Valeurs par défaut
  if (page.default) store.dispatch(Actions.DEFAULT_VALUES_SET, page.default);

  // Alias des datasets
  if (page.datasets)
    Object.keys(page.datasets).forEach((alias) => {
      store.dispatch(Actions.DATASET_SET, {
        name: alias,
        key: AppConstants.DATASET_REAL_NAME_KEY,
        value: page?.datasets?.[alias],
      });
    });

  // Rafraîchissement de la page
  window.addEventListener('resize', () => {
    store.commit(Mutations.UPDATE_SWITCH_CHANGE);
  });

  // Initialisation page
  if (page.init) await page.init(store);

  return store;
};

export const useStore = () => {
  const instance = getCurrentInstance();
  return instance?.proxy.$store as Store<State>;
};
